import { toInputDate } from "@/utils/dateUtils";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/context/LanguageContext";
import { auctionsLabels, carDetailsLabels } from "@/i18n";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useCallback, useState } from "react";
import { MyAuctionsStatsDialog } from "./MyAuctionsStatsDialog";
import { ChevronDown, ChevronUp, ExternalLink, FileSignature } from "lucide-react";
import { SellerContractPopup } from "./SellerContractPopup";
import { LicensePlate } from "./LicensePlate";
import { SaleTypeBadge } from "@/components/ui/SaleTypeBadge";
import { ListingMyListings } from "@/types/listing";
import { ListingService } from "@/services/listing/listingService";
import { useAuth } from "@/hooks/useAuth";
import { UserType } from "@/types/user";
import { buildAuctionListingPath } from "@/lib/listingUrl";
import { sellerContractNeedsSignature } from "@/lib/sellerContractStatus";

interface MyAuctionsCardsProps {
  listings: ListingMyListings[];
  selectedVehicle: string | null;
  setSelectedVehicle: (id: string | null) => void;
}

export const MyAuctionsCards = ({
  listings,
  selectedVehicle,
  setSelectedVehicle,
}: MyAuctionsCardsProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const ma = auctionsLabels(language).myAuctions;
  const bi = carDetailsLabels(language).biddingInfo;
  const isAdmin = user?.type === UserType.Admin;
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [selectedListingForStats, setSelectedListingForStats] =
    useState<ListingMyListings | null>(null);
  const [statsData, setStatsData] = useState<any>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [contractPopupListingId, setContractPopupListingId] = useState<
    string | null
  >(null);
  const sc = (ma.sellerContract ?? {}) as Record<string, string>;
  const contractPopupListing =
    listings.find((l) => l.id === contractPopupListingId) ?? null;

  const renderSellerContractButton = (listing: ListingMyListings) => {
    const contract = listing.sellerContract;
    if (!contract) return null;
    const needsSignature = sellerContractNeedsSignature(listing);
    const isSigned = contract.signatureStatus === "signed";
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          setContractPopupListingId(listing.id);
        }}
        className="flex items-center gap-1.5 text-xs h-7 px-0"
      >
        <FileSignature className="h-3 w-3" />
        <span>{sc.viewContract}</span>
        {needsSignature && (
          <span className="relative flex h-2 w-2" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
          </span>
        )}
        <Badge
          variant="default"
          className={`py-0 text-[10px] ${
            isSigned
              ? "bg-green-100 text-green-800 border-green-200"
              : needsSignature
                ? "bg-amber-100 text-amber-800 border-amber-200"
                : "bg-gray-100 text-gray-800 border-gray-200"
          }`}
        >
          {isSigned
            ? sc.badgeSigned
            : needsSignature
              ? sc.badgeSignatureRequired
              : sc.badgePending}
        </Badge>
      </Button>
    );
  };

  const handleListingDetailsClick = useCallback(
    (e: React.MouseEvent, listingId: string) => {
      e.stopPropagation();
      const listing = listings.find((l) => l.id === listingId);
      if (listing && listing.campaignId) {
        navigate(buildAuctionListingPath(listing.campaignId, listing), {
          state: { from: "/my-auctions" },
        });
      }
    },
    [navigate, listings],
  );

  const handleViewStats = async (
    e: React.MouseEvent,
    listing: ListingMyListings,
  ) => {
    e.stopPropagation();
    setSelectedListingForStats(listing);
    setIsLoadingStats(true);
    setStatsDialogOpen(true);

    try {
      // Fetch real bids from backend
      const bidsResponse = await ListingService.getListingBids(listing.id);
      const bids = bidsResponse.data || [];

      // Get highest bid amount
      const highestBidAmount =
        bids.length > 0 ? Math.max(...bids.map((b) => b.amount || 0)) : 0;

      // Map bids to format expected by dialog, sorted by amount descending
      const sortedBids = [...bids].sort((a, b) => b.amount - a.amount);
      const bidHistory = sortedBids.map((bid, index) => {
        const isHighest = index === 0 && bid.amount === highestBidAmount; // First bid (highest) is the highest
        let bidderName = "";

        if (isAdmin && bid.bidderName) {
          // Admin sees full name
          bidderName = bid.bidderName;
        } else {
          // Normal users see only stars, no first letter
          bidderName = "•".repeat(8);
        }

        return {
          id: bid.id,
          bidder: bidderName,
          amount: `${bid.amount.toLocaleString("sv-SE")} :-`,
          timestamp: new Date(bid.createdAt).toLocaleString("sv-SE", {
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          }),
          isHighest,
          ...(isAdmin && bid.bidderEmail ? { email: bid.bidderEmail } : {}),
        };
      });

      // Calculate unique bidders
      const uniqueBidderIds = new Set(bids.map((b) => b.bidderId));
      const uniqueBidders = uniqueBidderIds.size;

      const isAuction = listing.listingType === "auction";
      const isCompleted = listing.status === "completed";
      const isBuyNowTriggered =
        listing.auctionDetails?.isBuyNowTriggered === true;
      const auctionCurrentPrice =
        isCompleted && isBuyNowTriggered
          ? (listing.auctionDetails?.currentBuyNowPrice ??
            listing.auctionDetails?.buyNowFixed)
          : listing.bidding?.highestBid ||
            listing.auctionDetails?.currentBid ||
            0;
      const currentPrice = isAuction
        ? auctionCurrentPrice || 0
        : listing.directPurchaseDetails?.currentPrice || 0;
      const startingPrice =
        (isAuction && listing.auctionDetails?.startingPrice) || 0;

      setStatsData({
        totalViews: listing.bidding?.views || listing.views || 0,
        uniqueViewers: listing.bidding?.uniqueViews || 0,
        totalBids: bids.length,
        uniqueBidders,
        currentPrice: `${currentPrice.toLocaleString("sv-SE")} :-`,
        startingPrice: `${startingPrice.toLocaleString("sv-SE")} :-`,
        requestedPrice: listing.requestedPrice
          ? `${listing.requestedPrice.toLocaleString("sv-SE")} :-`
          : undefined,
        reservationPrice:
          listing.listingType === "auction" &&
          listing.auctionDetails?.reservationPrice != null &&
          listing.auctionDetails.reservationPrice > 0
            ? `${listing.auctionDetails.reservationPrice.toLocaleString("sv-SE")} :-`
            : undefined,
        endDate: listing.endTime
          ? toInputDate(new Date(listing.endTime))
          : isCompleted
            ? ma.card.completed
            : ma.card.ongoing,
        bids: bidHistory,
      });
    } catch (error) {
      // Fallback to using listing data
      setStatsData(getStatsFromListing(listing));
    } finally {
      setIsLoadingStats(false);
    }
  };

  // Generate stats data using real listing data
  const getStatsFromListing = (listing: ListingMyListings) => {
    const isAuction = listing.listingType === "auction";
    const isCompleted = listing.status === "completed";

    if (listing.status === "active" && !isCompleted) {
      // Active listings
      const currentPrice = isAuction
        ? listing.bidding?.highestBid || listing.auctionDetails?.currentBid || 0
        : listing.directPurchaseDetails?.currentPrice || 0;
      const startingPrice =
        (isAuction && listing.auctionDetails?.startingPrice) || 0;

      // Generate realistic bid history
      const bidHistory = [];
      const totalBids = listing.bidding?.totalBids || 0;

      if (totalBids > 0 && isAuction) {
        const bidIncrement = Math.max(
          1000,
          Math.floor(
            (currentPrice - startingPrice) / Math.max(1, totalBids - 1),
          ),
        );

        for (let i = 0; i < totalBids; i++) {
          const bidAmount = startingPrice + i * bidIncrement;
          const isHighest = i === totalBids - 1;

          bidHistory.push({
            id: i + 1,
            bidder: isHighest
              ? ma.card.highestBidder
              : `${ma.card.bidder} ${i + 1}`,
            amount: `${bidAmount.toLocaleString()} :-`,
            timestamp: new Date(
              Date.now() - (totalBids - i) * 24 * 60 * 60 * 1000,
            ).toLocaleString("sv-SE", {
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            }),
            isHighest,
          });
        }

        // Sort bids so highest bid appears first
        bidHistory.sort((a, b) => {
          const amountA = parseInt(a.amount.replace(/[^\d]/g, ""));
          const amountB = parseInt(b.amount.replace(/[^\d]/g, ""));
          return amountB - amountA;
        });
      }

      return {
        totalViews: listing.bidding?.views || 0,
        uniqueViewers: listing.bidding?.uniqueViews || 0,
        totalBids: totalBids,
        uniqueBidders: Math.min(
          totalBids,
          Math.max(1, Math.floor(totalBids * 0.8)),
        ),
        currentPrice: `${currentPrice.toLocaleString()} :-`,
        startingPrice: `${startingPrice.toLocaleString()} :-`,
        requestedPrice: listing.requestedPrice
          ? `${listing.requestedPrice.toLocaleString("sv-SE")} :-`
          : undefined,
        reservationPrice:
          listing.listingType === "auction" &&
          listing.auctionDetails?.reservationPrice != null &&
          listing.auctionDetails.reservationPrice > 0
            ? `${listing.auctionDetails.reservationPrice.toLocaleString("sv-SE")} :-`
            : undefined,
        endDate: listing.endTime
          ? toInputDate(new Date(listing.endTime))
          : bi.ongoing,
        bids: bidHistory,
      };
    } else {
      // Completed listings
      const isBuyNowTriggered =
        listing.auctionDetails?.isBuyNowTriggered === true;
      const soldPrice = isAuction
        ? (isBuyNowTriggered
            ? (listing.auctionDetails?.currentBuyNowPrice ??
              listing.auctionDetails?.buyNowFixed)
            : listing.bidding?.highestBid ||
              listing.auctionDetails?.currentBid) || 0
        : listing.directPurchaseDetails?.currentPrice || 0;

      const totalViews = Math.floor(Math.random() * 50) + 30;
      const totalBids = Math.floor(Math.random() * 8) + 3;

      // Generate realistic bid history for sold vehicle
      const bidHistory = [];
      if (totalBids > 0 && isAuction) {
        const startingAmount = Math.floor(soldPrice * 0.7);
        const bidIncrement = Math.max(
          1000,
          Math.floor((soldPrice - startingAmount) / Math.max(1, totalBids - 1)),
        );

        for (let i = 0; i < totalBids; i++) {
          const bidAmount = startingAmount + i * bidIncrement;
          const isHighest = i === totalBids - 1;

          bidHistory.push({
            id: i + 1,
            bidder: isHighest
              ? listing.winnerId
                ? `${ma.card.buyer} ${listing.winnerId.slice(-4)}`
                : ma.card.highestBidder
              : `${ma.card.bidder} ${i + 1}`,
            amount: `${bidAmount.toLocaleString()} :-`,
            timestamp: new Date(
              new Date(listing.endTime || Date.now()).getTime() -
                (totalBids - i) * 24 * 60 * 60 * 1000,
            ).toLocaleString("sv-SE", {
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            }),
            isHighest,
          });
        }

        // Sort bids so highest bid appears first
        bidHistory.sort((a, b) => {
          const amountA = parseInt(a.amount.replace(/[^\d]/g, ""));
          const amountB = parseInt(b.amount.replace(/[^\d]/g, ""));
          return amountB - amountA;
        });
      }

      return {
        totalViews,
        uniqueViewers: Math.floor(totalViews * 0.7),
        totalBids,
        uniqueBidders: Math.min(
          totalBids,
          Math.max(1, Math.floor(totalBids * 0.8)),
        ),
        currentPrice: `${soldPrice.toLocaleString()} :-`,
        startingPrice: `${Math.floor(soldPrice * 0.7).toLocaleString()} :-`,
        requestedPrice: listing.requestedPrice
          ? `${listing.requestedPrice.toLocaleString("sv-SE")} :-`
          : undefined,
        reservationPrice:
          listing.listingType === "auction" &&
          listing.auctionDetails?.reservationPrice != null &&
          listing.auctionDetails.reservationPrice > 0
            ? `${listing.auctionDetails.reservationPrice.toLocaleString("sv-SE")} :-`
            : undefined,
        endDate: listing.endTime
          ? toInputDate(new Date(listing.endTime))
          : ma.card.completed,
        bids: bidHistory,
      };
    }
  };

  if (listings.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">{ma.card.noListings}</div>
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-4 w-full">
      {listings.map((listing, index) => {
        const isAuction = listing.listingType === "auction";
        const isCompleted = listing.status === "completed";
        const isReserved = listing.status === "reserved";
        const isBuyNowTriggered =
          listing.auctionDetails?.isBuyNowTriggered === true;

        // Calculate current price - use auctionCurrentBid if available, otherwise highestBid, otherwise startingPrice
        const auctionCurrentPrice =
          isCompleted || isReserved
            ? isBuyNowTriggered
              ? (listing.auctionDetails?.currentBuyNowPrice ??
                listing.auctionDetails?.buyNowFixed)
              : listing.auctionDetails?.currentBid ||
                listing.bidding?.highestBid
            : listing.auctionDetails?.currentBid || listing.bidding?.highestBid;
        const currentPrice = isAuction
          ? (auctionCurrentPrice ?? listing.auctionDetails?.startingPrice ?? 0)
          : listing.directPurchaseDetails?.currentPrice || 0;

        const priceText = `${(currentPrice || 0).toLocaleString()} kr`;

        return (
          <motion.div
            key={listing.id}
            initial={{ opacity: 0, x: -20, y: 20 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="w-full"
          >
            <Card
              className={`glass-card hover-scale shadow-md hover:shadow-lg transition-shadow duration-200 w-full max-w-full overflow-hidden ${
                sellerContractNeedsSignature(listing)
                  ? "ring-2 ring-amber-400 border-amber-300"
                  : ""
              }`}
            >
              <CardContent className="p-0">
                {/* Main Content: Image left, Info right */}
                <div className="flex flex-col md:flex-row">
                  {/* Image - Left */}
                  {listing.thumbnail && (
                    <div className="relative flex-shrink-0 h-40 md:h-auto w-full md:w-2/5 overflow-hidden">
                      <img
                        src={listing.thumbnail}
                        alt={`${listing.vehicleDetails?.make} ${listing.vehicleDetails?.model}`}
                        className="w-full h-full object-cover"
                        style={{
                          minHeight: "100%",
                          maxHeight: "180px",
                          objectPosition: "center",
                        }}
                        loading="lazy"
                      />
                    </div>
                  )}

                  {/* Info - Right */}
                  <div className="flex-1 p-3 md:p-4 flex flex-col">
                    {/* Mobile Layout */}
                    <div className="md:hidden flex flex-col gap-1.5">
                      {/* Title with button */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {listing.title}
                          </h3>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {listing.vehicleDetails?.make}{" "}
                            {listing.vehicleDetails?.model}{" "}
                            {listing.vehicleDetails?.year}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs gap-1 flex-shrink-0 h-7 px-2"
                          onClick={(e) =>
                            handleListingDetailsClick(e, listing.id)
                          }
                        >
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </div>

                      <div className="flex items-center gap-1.5 flex-wrap">
                        {listing.vehicleDetails?.regNumber && (
                          <LicensePlate
                            lpn={listing.vehicleDetails.regNumber}
                          />
                        )}
                        <SaleTypeBadge saleType={listing.listingType} />
                      </div>

                      {/* 2x2 Grid for mobile */}
                      <div className="grid grid-cols-2 gap-2 mt-1">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {ma.card.status}
                          </span>
                          <Badge
                            variant="default"
                            className={`${
                              isCompleted
                                ? "bg-green-100 text-green-800 border-green-200"
                                : isReserved
                                  ? "bg-yellow-100 text-yellow-800 border-yellow-200"
                                  : "bg-blue-100 text-blue-800 border-blue-200"
                            } text-xs w-fit py-0`}
                          >
                            {isCompleted
                              ? ma.card.sold
                              : isReserved
                                ? ma.card.reserved
                                : listing.status === "draft"
                                  ? ma.card.draft
                                  : ma.card.active}
                          </Badge>
                        </div>

                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {ma.card.views}
                          </span>
                          <span className="text-xs font-semibold text-primary">
                            {listing.bidding?.views || 0}
                          </span>
                        </div>

                        <div className="flex flex-col gap-0.5 col-span-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {isCompleted
                              ? ma.card.endedWith
                              : isAuction
                                ? ma.card.currentBid
                                : ma.card.price}
                          </span>
                          <span className="font-bold text-green-500 text-sm">
                            {priceText}
                          </span>
                        </div>
                        <div className="flex items-center justify-between col-span-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {ma.card.updated}
                          </span>
                          <span className="text-xs text-gray-600 dark:text-gray-300 tabular-nums">
                            {listing.updatedAt
                              ? new Date(listing.updatedAt).toLocaleString(
                                  "sv-SE",
                                  {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  },
                                )
                              : ma.card.unknown}
                          </span>
                        </div>
                      </div>

                      {/* Visa detaljer button - mobile */}
                      <div className="flex items-center justify-start gap-4 flex-wrap mt-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setSelectedVehicle(
                              selectedVehicle === listing.id
                                ? null
                                : listing.id,
                            )
                          }
                          className="flex items-center gap-1.5 text-xs h-7 px-0"
                        >
                          {selectedVehicle === listing.id ? (
                            <>
                              <ChevronUp className="h-3 w-3" />
                              <span>{ma.card.hide}</span>
                            </>
                          ) : (
                            <>
                              <ChevronDown className="h-3 w-3" />
                              <span>{ma.card.showDetails}</span>
                            </>
                          )}
                        </Button>
                        {renderSellerContractButton(listing)}
                      </div>
                    </div>

                    {/* Desktop Layout */}
                    <div className="hidden md:grid md:grid-cols-2 gap-3 md:gap-4">
                      {/* Left Column */}
                      <div className="flex flex-col gap-1.5">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                          {listing.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {listing.vehicleDetails?.make}{" "}
                          {listing.vehicleDetails?.model}{" "}
                          {listing.vehicleDetails?.year}
                        </p>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {listing.vehicleDetails?.regNumber && (
                            <LicensePlate
                              lpn={listing.vehicleDetails.regNumber}
                            />
                          )}
                          <SaleTypeBadge saleType={listing.listingType} />
                        </div>
                      </div>

                      {/* Right Column */}
                      <div className="flex flex-col gap-1.5">
                        {/* Button at top right */}
                        <div className="flex justify-end mb-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs gap-1 flex-shrink-0 h-7 px-2"
                            onClick={(e) =>
                              handleListingDetailsClick(e, listing.id)
                            }
                          >
                            <ExternalLink className="w-3 h-3" />
                            <span>{ma.card.goToListing}</span>
                          </Button>
                        </div>

                        {/* Status and Price Details */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {ma.card.status}
                          </span>
                          <Badge
                            variant="default"
                            className={
                              isCompleted
                                ? "bg-green-100 text-green-800 border-green-200"
                                : isReserved
                                  ? "bg-yellow-100 text-yellow-800 border-yellow-200"
                                  : "bg-blue-100 text-blue-800 border-blue-200"
                            }
                          >
                            {isCompleted
                              ? ma.card.sold
                              : isReserved
                                ? ma.card.reserved
                                : listing.status === "draft"
                                  ? ma.card.draft
                                  : ma.card.active}
                          </Badge>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {ma.card.updated}
                          </span>
                          <span className="text-xs text-gray-600 dark:text-gray-300 tabular-nums">
                            {listing.updatedAt
                              ? new Date(listing.updatedAt).toLocaleString(
                                  "sv-SE",
                                  {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  },
                                )
                              : ma.card.unknown}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {ma.card.views}
                          </span>
                          <span className="text-xs font-semibold text-primary">
                            {listing.bidding?.views || 0}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {isCompleted
                              ? ma.card.endedWith
                              : isAuction
                                ? ma.card.currentBid
                                : ma.card.price}
                          </span>
                          <span className="font-bold text-green-500 text-sm">
                            {priceText}
                          </span>
                        </div>
                      </div>

                      {/* Visa detaljer button - inside right column */}
                      <div className="flex items-center justify-start gap-4 flex-wrap mt-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setSelectedVehicle(
                              selectedVehicle === listing.id
                                ? null
                                : listing.id,
                            )
                          }
                          className="flex items-center gap-1.5 text-xs h-7 px-0"
                        >
                          {selectedVehicle === listing.id ? (
                            <>
                              <ChevronUp className="h-3 w-3" />
                              <span className="hidden md:inline">
                                {ma.card.hideDetails}
                              </span>
                              <span className="md:hidden">{ma.card.hide}</span>
                            </>
                          ) : (
                            <>
                              <ChevronDown className="h-3 w-3" />
                              <span className="hidden md:inline">
                                {ma.card.showDetails}
                              </span>
                              <span className="md:hidden">{ma.card.show}</span>
                            </>
                          )}
                        </Button>
                        {renderSellerContractButton(listing)}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>

              {selectedVehicle === listing.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  className="px-3 md:px-4 pb-3 md:pb-4 pt-2 md:pt-3 border-t border-white/10 space-y-3 md:space-y-4 w-full"
                >
                  <div className="flex flex-row justify-between">
                    <div className="flex flex-col">
                      <h4 className="font-semibold mb-2">{ma.card.details}</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>{ma.card.created}</span>
                          <span>
                            {listing.createdAt
                              ? new Date(listing.createdAt).toLocaleDateString(
                                  "sv-SE",
                                )
                              : ma.card.unknown}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>{ma.card.updated}</span>
                          <span>
                            {listing.updatedAt
                              ? new Date(listing.updatedAt).toLocaleString(
                                  "sv-SE",
                                  {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  },
                                )
                              : ma.card.unknown}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>{ma.card.startPrice}</span>
                          <span>
                            {isAuction
                              ? `${(
                                  listing.auctionDetails?.startingPrice || 0
                                ).toLocaleString()} kr`
                              : `${(
                                  listing.directPurchaseDetails?.currentPrice ||
                                  0
                                ).toLocaleString()} kr`}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>{ma.card.endDate}</span>
                          <span>
                            {listing.endTime
                              ? ` ${new Date(
                                  listing.endTime,
                                ).toLocaleDateString("sv-SE")}`
                              : ma.card.ongoing}
                          </span>
                        </div>
                        {isCompleted && listing.winnerId && (
                          <div className="flex justify-between">
                            <span>{ma.card.buyer}</span>
                            <span>
                              {ma.card.buyer.replace(/:\s*$/, "")}{" "}
                              {listing.winnerId.slice(-4)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    {isAuction && (
                      <div className="flex justify-center items-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => handleViewStats(e, listing)}
                          className="flex items-center gap-2"
                        >
                          {ma.card.viewFullStats}
                        </Button>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </Card>
          </motion.div>
        );
      })}

      {contractPopupListing?.sellerContract && (
        <SellerContractPopup
          listing={contractPopupListing}
          onClose={() => setContractPopupListingId(null)}
        />
      )}

      {selectedListingForStats && (
        <MyAuctionsStatsDialog
          open={statsDialogOpen}
          onOpenChange={setStatsDialogOpen}
          stats={statsData || getStatsFromListing(selectedListingForStats)}
          vehicleName={`${selectedListingForStats.vehicleDetails?.make} ${selectedListingForStats.vehicleDetails?.model} ${selectedListingForStats.vehicleDetails?.year}`}
          regNumber={selectedListingForStats.vehicleDetails?.regNumber}
          isLoading={isLoadingStats}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
};
